/**
 * The API client.
 *
 * `VITE_API_BASE_URL` is inlined by Vite at BUILD time. Empty (the local
 * default) means same-origin, which the dev proxy turns into localhost:3001.
 * In production it is the backend's public origin and CORS applies.
 */
import type { PingResponse } from "./types.ts";

const BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/** An API call that came back with a non-2xx status. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The error shapes the backend returns, all optional. */
interface ErrorBody {
  error?: string;
  detail?: Record<string, string[]>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { "content-type": "application/json", ...init?.headers },
    });
  } catch {
    // fetch only rejects on a transport failure — DNS, refused, CORS blocked.
    throw new ApiError(0, `cannot reach the API${BASE ? ` at ${BASE}` : ""}`);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ErrorBody;
    // Prefer a field-level validation message; it is the actionable one.
    const detail = Object.values(body.detail ?? {})
      .flat()
      .join(", ");
    throw new ApiError(res.status, detail || body.error || `request failed (${res.status})`);
  }

  return (await res.json()) as T;
}

export const api = {
  /** Reachability, plus the last tick the scheduled job recorded. */
  ping: () => request<PingResponse>("/api/ping"),
};
